import { Component, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription, catchError, tap } from 'rxjs';

import { FiltroTabelasAdministrativas } from '@tabelasAdministrativas/models/filtro';
import { SoftskillApiService } from '../services/softskill-api.service';
import { TabelasAdministrativas } from '@core/models/tabelasAdministrativas';
import { UiService } from '@core/services/ui.service';

@Component({
	templateUrl: './tabela-softskill.component.html',
	providers: [MessageService, ConfirmationService]
})
export class TabelaSoftskillComponent implements OnDestroy {
	pesquisarAoCarregar = true;
	habilitarFiltroCadastradoUsuario = false;
	filtros?: FiltroTabelasAdministrativas;
	limparFormularioPesquisa = false;
	resultado: any[] = [];
	loadingResultado = false;
	totalResultado?: number;
	pagina = 1;
	itensPorPagina = 10;
	primeiroItemResultado = 0;
	colunas: any;
	itemTabela = new TabelasAdministrativas();
	operacao?: string;
	exibirModalManterTabela = false;
	tituloModalManterTabela = '';
	subscriptions: Subscription[] = [];
	registrosSelecionados: TabelasAdministrativas[] = [];

	constructor(
		private confirmationService: ConfirmationService,
		private tabelaService: SoftskillApiService,
		private messageService: MessageService,
		private ui: UiService
	) {}

	ngOnDestroy(): void {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
	}

	prepararCadastrar() {
		this.tituloModalManterTabela = 'Cadastrar softskill';
		this.itemTabela = new TabelasAdministrativas();
		this.operacao = 'cadastrar';
		this.exibirModalManterTabela = true;
	}

	prepararAlterar(event: any) {
		this.subscriptions.push(
			this.tabelaService.obterSoftskill(event.id).subscribe(res => {
				this.itemTabela = new TabelasAdministrativas(res.id, res.nome, res.ativo);
				this.tituloModalManterTabela = 'Alterar softskill';
				this.operacao = 'alterar';
				this.exibirModalManterTabela = true;
			})
		);
	}

	ativarInativarEmLote(ativar = true) {
		this.messageService.clear();
		if (this.registrosSelecionados.length === 0)
			return;

		const listaIdRegistrosSelecionados: number[] = [];
		for (const item of this.registrosSelecionados) {
			listaIdRegistrosSelecionados.push(item.id!);
		}
		const operacao = ativar ? 'ativado(s)' : 'inativado(s)';
		this.subscriptions.push(
			this.tabelaService
				.ativarInativarSoftskill(listaIdRegistrosSelecionados!, ativar)
				.pipe(
					tap(() => {
						this.criarMensagem('success', `Softskill(s) ${operacao} com sucesso`);
						this.pesquisar();
					}),
					catchError(err => {
						this.criarMensagem('error', err.error.message);
						return [];
					})
				)
				.subscribe()
		);
	}

	salvarItem(event: TabelasAdministrativas) {
		const softskill = new TabelasAdministrativas();
		softskill.id = event.id;
		softskill.nome = event.nome;
		softskill.ativo = event.ativo ? true : false;
		if (this.operacao === 'cadastrar') {
			this.salvarNovo(softskill);
		} else if (softskill.id) {
			this.alterar(softskill.id, softskill);
		}
		this.operacao = '';
		this.exibirModalManterTabela = false;
	}

	executarPesquisar(event: any) {
		this.filtros = new FiltroTabelasAdministrativas(undefined, event.nome, event.ativo, event.cadastradoUsuario);
		this.primeiroItemResultado = 0;
		this.pagina = 1;
		this.messageService.clear();
		this.pesquisar();
		this.criarColunas();
	}

	limparFormulario() {
		if (this.pesquisarAoCarregar) {
			this.executarPesquisar(new FiltroTabelasAdministrativas());
			this.limparFormularioPesquisa = true;
		} else {
			this.resultado = [];
		}
	}

	excluirItem(event: any) {
		const softskill = event;
		this.confirmationService.confirm({
			message: 'Esta ação não poderá ser desfeita.',
			header: 'Deseja excluir o softskill ' + softskill.nome + '?',
			accept: async () => {
				this.tabelaService.excluirSoftskill(event.id).subscribe(
					async (res: any) => {
						this.exibirMensagemSucesso(res);
					},
					err => {
						this.criarMensagem('error', err.error.message);
					}
				);
			}
		});
	}

	fecharModal() {
		this.exibirModalManterTabela = false;
		this.itemTabela = new TabelasAdministrativas();
		this.operacao = 'cadastrar';
	}

	paginar(event: any) {
		this.pagina = event.first / event.rows + 1;
		this.primeiroItemResultado = event.first;
		this.itensPorPagina = event.rows;
		this.pesquisar();
	}

	getItensPorPagina(event: any) {
		this.itensPorPagina = event.rows;
	}

	private pesquisar() {
		this.resultado = [];
		this.loadingResultado = true;
		if (this.filtros) {
			this.subscriptions.push(
				this.tabelaService.obterSoftskills(this.filtros, this.pagina, this.itensPorPagina).subscribe(
					(resultado: any) => {
						this.loadingResultado = false;
						this.totalResultado = resultado.metadata.total;
						this.pagina = resultado.metadata.page;
						this.tratarDados(resultado.items);
					},
					err => {
						this.totalResultado = 0;
						this.resultado = [];
						this.criarMensagem('error', err.error.message);
					}
				)
			);
		}
	}

	private criarColunas() {
		this.colunas = [
			{ field: 'id', header: 'id' },
			{ field: 'nome', header: 'Softskill' },
			{ field: 'ativo', header: 'Ativo' }
		];
	}

	private tratarDados(itens: any[]) {
		for (const item of itens) {
			const resultadoTratado = {
				id: item.id,
				nome: item.nome,
				ativo: item.ativo ? 'Sim' : 'Não'
			};
			this.resultado.push(resultadoTratado);
		}
	}

	private salvarNovo(softskill: TabelasAdministrativas) {
		this.subscriptions.push(
			this.tabelaService.incluirSoftskill(softskill).subscribe(
				(res: any) => {
					this.exibirMensagemSucesso(res);
				},
				err => {
					this.criarMensagem('error', err.error.message);
				}
			)
		);
	}

	private alterar(id: number, softskill: TabelasAdministrativas) {
		this.subscriptions.push(
			this.tabelaService.alterarSoftskill(id, softskill).subscribe(
				(res: any) => {
					this.exibirMensagemSucesso(res);
				},
				err => {
					this.criarMensagem('error', err.error.message);
				}
			)
		);
	}

	private criarMensagem(criticidade: string, mensagem: string) {
		this.messageService.clear();
		this.messageService.add(this.ui.criarMensagem(criticidade, '', mensagem));
	}

	private exibirMensagemSucesso(res: any) {
		if (res) {
			this.criarMensagem('success', res.message);
			this.limparPesquisa();
		}
	}

	private limparPesquisa() {
		this.limparFormularioPesquisa = true;
		this.primeiroItemResultado = 0;
		this.pagina = 1;
		this.pesquisar();
	}
}
